// ABOUTME: Admin Panel account management page — create, list, change role, and delete accounts (super_admin only).
import { ADMIN_ROLES, AdminRole } from '../../../types/adminRoles';
import { Account } from '../api/accountsApi';
import { ChangeHistoryList } from '../components/admin/ChangeHistoryList';
import { ServiceAccountToken } from '../components/admin/ServiceAccountToken';
import { ConfirmationModal } from '../components/ui/confirmation-modal';
import {
  useAccounts,
  useCreateAccount,
  useUpdateAccountRole,
  useDeleteAccount,
} from '../hooks/useAccountQueries';
import { useAuthStore } from '../store/authStore';
import './AccountsPage.css';
import { AxiosError } from 'axios';
import { useState } from 'react';

const ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  user: 'User',
};

function apiErrorMessage(error: unknown, fallback: string): string {
  const message = (error as AxiosError<{ error?: string }>)?.response?.data
    ?.error;
  return message || fallback;
}

export default function AccountsPage() {
  const currentUser = useAuthStore((state) => state.user);
  const { data, isLoading, error } = useAccounts();
  const createAccount = useCreateAccount();
  const updateRole = useUpdateAccountRole();
  const deleteAccount = useDeleteAccount();

  const [form, setForm] = useState({
    email: '',
    name: '',
    password: '',
    role: 'admin' as AdminRole,
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{
    id: string;
    message: string;
  } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Account | null>(null);
  const [historyAccount, setHistoryAccount] = useState<Account | null>(null);

  const accounts = data?.accounts ?? [];

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    try {
      await createAccount.mutateAsync(form);
      setForm({ email: '', name: '', password: '', role: 'admin' });
    } catch (err) {
      setFormError(apiErrorMessage(err, 'Failed to create account.'));
    }
  };

  const handleRoleChange = async (account: Account, role: AdminRole) => {
    if (role === account.role) return;
    setRowError(null);
    try {
      await updateRole.mutateAsync({ id: account.id, role });
    } catch (err) {
      setRowError({
        id: account.id,
        message: apiErrorMessage(err, 'Failed to update role.'),
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDelete) return;
    setRowError(null);
    try {
      await deleteAccount.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
    } catch (err) {
      setRowError({
        id: pendingDelete.id,
        message: apiErrorMessage(err, 'Failed to delete account.'),
      });
      setPendingDelete(null);
    }
  };

  return (
    <div className="page-container accounts-page">
      <div className="hero-gradient">
        <h1 className="trustflight-heading">Admin Panel Accounts</h1>
        <p>Create staff accounts and manage roles for the Admin Panel.</p>
      </div>

      <div className="card accounts-form-card">
        <h2 className="trustflight-heading card-title">New Account</h2>
        <form onSubmit={handleCreate} className="accounts-form">
          <div className="form-group">
            <label className="form-label" htmlFor="account-name">
              Name
            </label>
            <input
              id="account-name"
              className="input-field"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="account-email">
              Email
            </label>
            <input
              id="account-email"
              type="email"
              className="input-field"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="account-password">
              Password
            </label>
            <input
              id="account-password"
              type="password"
              className="input-field"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="account-role">
              Role
            </label>
            <select
              id="account-role"
              className="input-field"
              value={form.role}
              onChange={(e) =>
                setForm({ ...form, role: e.target.value as AdminRole })
              }
            >
              {ADMIN_ROLES.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </select>
          </div>
          {formError && <p className="accounts-error">{formError}</p>}
          <button
            type="submit"
            className="button-primary"
            disabled={createAccount.isPending}
          >
            {createAccount.isPending ? 'Creating...' : 'Create Account'}
          </button>
        </form>
      </div>

      <div className="card accounts-list-card">
        <h2 className="trustflight-heading card-title">Accounts</h2>
        {isLoading && <p>Loading accounts...</p>}
        {error && <p className="accounts-error">Failed to load accounts.</p>}
        {!isLoading && !error && (
          <table className="accounts-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td>
                    {account.name}
                    {account.id === currentUser?.id && (
                      <span className="accounts-you-tag">you</span>
                    )}
                  </td>
                  <td>{account.email}</td>
                  <td>
                    <select
                      className="input-field"
                      value={account.role}
                      disabled={updateRole.isPending}
                      onChange={(e) =>
                        handleRoleChange(account, e.target.value as AdminRole)
                      }
                    >
                      {ADMIN_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {ROLE_LABELS[role]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {account.createdAt
                      ? new Date(account.createdAt).toLocaleDateString()
                      : '—'}
                  </td>
                  <td>
                    <button
                      className="button-secondary subtle"
                      onClick={() => setHistoryAccount(account)}
                    >
                      History
                    </button>
                    <button
                      className="button-danger subtle"
                      onClick={() => setPendingDelete(account)}
                      disabled={deleteAccount.isPending}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {accounts.length === 0 && (
                <tr>
                  <td colSpan={5}>No accounts yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        {rowError && <p className="accounts-error">{rowError.message}</p>}
      </div>

      <ServiceAccountToken />

      <ConfirmationModal
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete account?"
        message={`This will permanently delete ${
          pendingDelete?.name ?? 'this account'
        }'s access to the Admin Panel.`}
        confirmLabel="Delete"
        type="danger"
        isLoading={deleteAccount.isPending}
      />

      {historyAccount && (
        <div
          className="accounts-history-overlay"
          onClick={() => setHistoryAccount(null)}
        >
          <div
            className="card accounts-history-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="accounts-history-header">
              <h2 className="trustflight-heading card-title">
                History — {historyAccount.name}
              </h2>
              <button
                className="button-secondary subtle"
                onClick={() => setHistoryAccount(null)}
              >
                Close
              </button>
            </div>
            <ChangeHistoryList
              collection="admins"
              documentId={historyAccount.id}
            />
          </div>
        </div>
      )}
    </div>
  );
}
